import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import Tabs from 'components/partials/tabs/tabs';
import toast from 'components/partials/toast/toast';
import CriticalElementHeader from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/critical-element-details-header/critical-element-details-header';
import CriticalElementForm from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/critical-element-form/critical-element-form';
import RelatedGoals from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/related-goals/related-goals';
import History from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/critical-element-tabs/critical-element-history-tab';
import {
  CriticalElementFormProvider,
  useCriticalElementForm,
} from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/context/context';

import api from 'api';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

import {
  getPathToElements,
  prepareElementPayload as preparePayload,
} from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-elements.helpers';
import { renderConfirmationModal } from 'helpers/confirmation-render';
import {
  getTabItems,
  getEmptyElement,
} from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/critical-element.helpers';

import Icons from 'assets/icons';

const CriticalElement = ({
  data,
  isEditMode,
  setIsEditMode,
  loading,
  getElement,
  onSaveSuccess,
  onCancel,
  setLoading,
}) => {
  const params = useParams();
  const { id: elementId, year } = params;

  const selectedElementId = elementId;
  const isElementCreation = elementId === 'new';

  const defaultValues = isElementCreation ? getEmptyElement(year) : data;

  return (
    <CriticalElementFormProvider
      defaultValues={defaultValues}
      loading={loading}
    >
      <CriticalElementInner
        data={data}
        isEditMode={isEditMode}
        isCreation={isElementCreation}
        setIsEditMode={setIsEditMode}
        selectedElementId={selectedElementId}
        getElement={getElement}
        onSaveSuccess={onSaveSuccess}
        onCancel={onCancel}
        year={year}
        setLoading={setLoading}
      />
    </CriticalElementFormProvider>
  );
};

const CriticalElementInner = ({
  data,
  isEditMode,
  isCreation,
  setIsEditMode,
  selectedElementId,
  getElement,
  onSaveSuccess,
  onCancel,
  year,
  setLoading,
}) => {
  const [element, setElement] = useState(data);
  const [activeTab, setActiveTab] = useState('details');
  const { getValues, handleSubmit, reset } = useCriticalElementForm();
  const relatedGoalsSaveRef = useRef(null);

  const params = useParams();
  const navigate = useNavigate();
  const clientId = params.clientId || params.id;

  const user = useCurrentUser();
  const userId = user?.data?.oktaUserId;

  useEffect(() => {
    if (isCreation && !isEditMode) {
      setIsEditMode(true);
    }
  }, [isCreation, isEditMode, setIsEditMode]);

  useEffect(() => {
    if (isCreation) {
      const newElement = getEmptyElement(year);

      setElement(newElement);
      reset(newElement);
    } else {
      setElement(data);
    }
    setActiveTab('details');
  }, [data, isCreation, reset, selectedElementId, year]);

  const handleEdit = useCallback(() => {
    setIsEditMode(true);
  }, [setIsEditMode]);

  const handleCancel = useCallback(() => {
    if (isCreation) {
      if (onCancel) {
        onCancel();

        return;
      }

      const pathToElements = getPathToElements(window.location.pathname);

      navigate(pathToElements);

      return;
    }

    setIsEditMode(false);
    if (data) {
      reset(data);
    }
  }, [setIsEditMode, reset, data, isCreation, navigate, onCancel]);

  const onSubmit = useCallback(async () => {
    setLoading(true);
    const formData = getValues();

    formData.enteringClassYear = year;

    const payload = preparePayload({
      userId,
      clientId,
      data: element,
      formData,
    });

    try {
      let savedElementId = element?.id;

      if (element?.id) {
        await api.updateCriticalElement({
          userId,
          clientId,
          id: element.id,
          elementData: payload.elementData,
        });

        toast.success({
          title: 'The critical element was successfully updated',
        });

        if (relatedGoalsSaveRef.current) {
          await relatedGoalsSaveRef.current();
        }

        await getElement();
        setIsEditMode(false);
      } else {
        const response = await api.createCriticalElement({
          userId,
          clientId,
          elementData: payload.elementData,
        });

        savedElementId = response?.data?.id;

        toast.success({
          title: 'The critical element was successfully created',
        });

        if (relatedGoalsSaveRef.current && savedElementId) {
          await relatedGoalsSaveRef.current(savedElementId);
        }

        if (onSaveSuccess) {
          onSaveSuccess();

          return;
        }

        const pathToElements = getPathToElements(window.location.pathname);

        navigate(pathToElements);
      }
    } catch (error) {
      console.error('Error saving critical element:', error);
      toast.error({
        title: 'Something went wrong',
      });
    }
  }, [
    getValues,
    element,
    userId,
    clientId,
    setIsEditMode,
    getElement,
    navigate,
    onSaveSuccess,
    relatedGoalsSaveRef,
    year,
  ]);

  const handleFormSubmit = handleSubmit(onSubmit);

  const handleDelete = useCallback(async () => {
    if (!element?.id) return;

    try {
      await api.deleteCriticalElement({
        userId,
        clientId,
        id: element.id,
      });

      toast.success({
        title: 'The critical element was successfully removed',
      });

      if (onSaveSuccess) {
        onSaveSuccess();

        return;
      }

      const pathToElements = getPathToElements(window.location.pathname);

      navigate(pathToElements);
    } catch (error) {
      console.error('Error deleting critical element:', error);
      toast.error({
        title: 'Something went wrong',
      });
    }
  }, [element?.id, userId, clientId, navigate, onSaveSuccess]);

  const getActionMenuItems = () => [
    {
      label: 'Delete critical element',
      icon: <Icons.Delete customClass="text-red-04 mr-2" />,
      isDelete: true,
      onClick: () =>
        renderConfirmationModal({
          headerText: `Are you sure you want to delete the critical element?`,
          onConfirmCallback: () => {
            handleDelete(element.id);
          },
          onConfirmButtonLabel: 'Delete critical element',
        }),
    },
  ];

  const renderFormContent = () => (
    <div className="flex gap-x-9">
      <div className="w-1/2">
        <CriticalElementForm
          data={element}
          isEditing={isEditMode}
          isCreation={isCreation}
          userId={userId}
        />
      </div>
      <div className="w-1/2">
        <RelatedGoals
          clientId={clientId}
          criticalElementId={element?.id}
          enteringClassYear={element?.enteringClassYear}
          isEditMode={isEditMode}
          onSave={relatedGoalsSaveRef}
        />
      </div>
    </div>
  );

  const renderHistory = () => (
    <History data={element} userId={userId} clientId={clientId} />
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'details':
        return renderFormContent();
      case 'history':
        return renderHistory();
      default:
        return null;
    }
  };

  return (
    <div className="w-full h-full pt-4">
      <CriticalElementHeader
        isEditMode={isEditMode}
        onEditClick={handleEdit}
        customClass="mb-5"
        isNew={isCreation}
        onCancel={handleCancel}
        onSubmit={handleFormSubmit}
        activeTab={activeTab}
        actionMenuItems={getActionMenuItems()}
        title={data?.summary}
      />
      <Tabs
        items={getTabItems(!element?.id || isEditMode)}
        activeTab={activeTab}
        onSelect={setActiveTab}
        customClass="mb-3"
      />

      {renderTabContent()}
    </div>
  );
};

export default CriticalElement;
